import { useMemo, useState } from 'react';
import {
  CalendarClock,
  ChevronDown,
  Goal,
  Medal,
  ShieldCheck,
  TrendingUp,
  Trophy,
  Upload,
  ScanText,
  CheckCircle2,
  AlertTriangle,
  X,
} from 'lucide-react';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';
import { ClubLogo } from './ClubLogo';
import { PREMIERSHIP_CLUBS, resolveBundledClub, normalizeClubName } from './leagueClubs';
import { LeagueClubDirectory } from './LeagueClubDirectory';
import type { ModuleId } from './AppShell';
import { isPlayerRole, isStaffRole } from './staffAccess';
import type { OfficialLeagueResult } from './types';



type SmartParsedResult = {
  home_team: string;
  home_goals: number;
  away_goals: number;
  away_team: string;
  raw: string;
};

const SMART_RESULTS_STORAGE_KEY = 'ecc_smart_results_2026_27';

function localDateInputValue() {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 10);
}

function candidateNames(club: (typeof PREMIERSHIP_CLUBS)[number]) {
  return [club.canonicalName, club.displayName, club.shortName, ...club.aliases]
    .map((value) => ({ value, normalized: normalizeClubName(value) }))
    .filter((entry) => entry.normalized.length >= 3)
    .sort((a, b) => b.normalized.length - a.normalized.length);
}

function resolveClubFromText(value: string) {
  const normalized = normalizeClubName(value);
  if (!normalized) return null;
  const direct = resolveBundledClub(value);
  if (direct) return direct;
  let best: { club: (typeof PREMIERSHIP_CLUBS)[number]; score: number } | null = null;
  PREMIERSHIP_CLUBS.forEach((club) => {
    candidateNames(club).forEach((candidate) => {
      let score = 0;
      if (normalized === candidate.normalized) score = 1000 + candidate.normalized.length;
      else if (normalized.includes(candidate.normalized)) score = 500 + candidate.normalized.length;
      else if (candidate.normalized.includes(normalized) && normalized.length >= 5) score = 250 + normalized.length;
      if (score && (!best || score > best.score)) best = { club, score };
    });
  });
  return best?.club || null;
}

function parseSmartResultsText(text: string): SmartParsedResult[] {
  const lines = text
    .replace(/\r/g, '\n')
    .split(/\n+/)
    .map((line) => line.replace(/[|•·]/g, ' ').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  const output: SmartParsedResult[] = [];
  const seen = new Set<string>();

  for (const line of lines) {
    const scores = [...line.matchAll(/\b(\d{1,2})\s*[-–—:]\s*(\d{1,2})\b/g)];
    for (const score of scores) {
      const index = score.index ?? -1;
      if (index < 0) continue;
      const left = line.slice(0, index).trim();
      const right = line.slice(index + score[0].length).trim();
      const home = resolveClubFromText(left);
      const away = resolveClubFromText(right);
      if (!home || !away || home.key === away.key) continue;
      const homeGoals = Number(score[1]);
      const awayGoals = Number(score[2]);
      if (homeGoals > 20 || awayGoals > 20) continue;
      const key = `${home.key}|${homeGoals}|${awayGoals}|${away.key}`;
      if (seen.has(key)) continue;
      seen.add(key);
      output.push({
        home_team: home.canonicalName,
        home_goals: homeGoals,
        away_goals: awayGoals,
        away_team: away.canonicalName,
        raw: line,
      });
    }
  }
  return output.slice(0, 8);
}

async function tryBrowserTextRecognition(file: File): Promise<string> {
  const Detector = (window as any).TextDetector;
  if (!Detector) return '';
  const bitmap = await createImageBitmap(file);
  try {
    const detector = new Detector();
    const blocks = await detector.detect(bitmap);
    return blocks.map((block: any) => block.rawValue || block.text || '').filter(Boolean).join('\n');
  } finally {
    bitmap.close?.();
  }
}

async function tryTesseractRecognition(file: File, progress: (message: string) => void): Promise<string> {
  const win = window as any;
  if (!win.Tesseract) {
    progress('Loading OCR engine…');
    await new Promise<void>((resolve, reject) => {
      const existing = document.querySelector('script[data-ecc-tesseract]') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('OCR engine failed to load')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
      script.async = true;
      script.dataset.eccTesseract = '1';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('OCR engine failed to load'));
      document.head.appendChild(script);
    });
  }
  if (!win.Tesseract?.recognize) return '';
  progress('Reading result card…');
  const result = await win.Tesseract.recognize(file, 'eng', {
    logger: (event: any) => {
      if (event?.status === 'recognizing text' && typeof event.progress === 'number') {
        progress(`Reading result card… ${Math.round(event.progress * 100)}%`);
      }
    },
  });
  return result?.data?.text || '';
}

type TableRow = {
  club: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  points: number;
  form: string[];
};

function resultCode(goalsFor: number, goalsAgainst: number) {
  if (goalsFor > goalsAgainst) return 'W';
  if (goalsFor === goalsAgainst) return 'D';
  return 'L';
}

export function LeagueCentre({
  onNavigate,
  onOpenPlayer,
}: {
  onNavigate: (module: ModuleId) => void;
  onOpenPlayer: (playerId: string) => void;
}) {
  const { t } = useI18n();
  const {
    team,
    players,
    matches,
    officialStandings,
    officialScorers,
    officialResults,
    officialLeagueSyncedAt,
    refresh,
    loading,
    error,
  } = useData();
  const [selectedClubKey, setSelectedClubKey] = useState('polonia-glasgow');
  const [smartImportOpen, setSmartImportOpen] = useState(false);
  const [smartFile, setSmartFile] = useState<File | null>(null);
  const [smartPreview, setSmartPreview] = useState('');
  const [smartText, setSmartText] = useState('');
  const [smartDate, setSmartDate] = useState(localDateInputValue());
  const [smartReading, setSmartReading] = useState(false);
  const [smartStatus, setSmartStatus] = useState('');
  const smartParsed = useMemo(() => parseSmartResultsText(smartText), [smartText]);
  const canImportResults = !isPlayerRole(team?.role) && !isStaffRole(team?.role);

  function openClubSheet(clubName: string) {
    const club = resolveBundledClub(clubName);
    if (!club) return;
    setSelectedClubKey(club.key);
    window.setTimeout(() => {
      document.getElementById('league-club-sheet')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 20);
  }


  function chooseSmartFile(file: File | null) {
    setSmartFile(file);
    setSmartText('');
    setSmartStatus('');
    if (smartPreview) URL.revokeObjectURL(smartPreview);
    setSmartPreview(file ? URL.createObjectURL(file) : '');
  }

  async function readSmartScreenshot() {
    if (!smartFile) return;
    setSmartReading(true);
    setSmartStatus('Reading screenshot…');
    try {
      let text = await tryBrowserTextRecognition(smartFile);
      if (!text.trim()) text = await tryTesseractRecognition(smartFile, setSmartStatus);
      if (!text.trim()) throw new Error('No text was recognised');
      setSmartText(text);
      const rows = parseSmartResultsText(text);
      setSmartStatus(rows.length ? `${rows.length} result${rows.length === 1 ? '' : 's'} detected. Verify before confirming.` : 'Text read, but no complete league result rows were detected. You can correct the text below.');
    } catch (error) {
      setSmartStatus(`${error instanceof Error ? error.message : 'OCR unavailable'}. On iPhone, use Live Text on the screenshot and paste the result text below.`);
    } finally {
      setSmartReading(false);
    }
  }

  async function confirmSmartResults() {
    if (!smartParsed.length || !smartDate) return;
    const now = new Date().toISOString();
    let existing: OfficialLeagueResult[] = [];
    try {
      const raw = localStorage.getItem(SMART_RESULTS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) existing = parsed;
    } catch {
      existing = [];
    }
    const rows: OfficialLeagueResult[] = smartParsed.map((result) => ({
      season: '2026/27',
      division: 'Only Sport Ltd Premiership',
      match_date: smartDate,
      competition: 'Only Sport Ltd Premiership',
      home_team: result.home_team,
      away_team: result.away_team,
      home_goals: result.home_goals,
      away_goals: result.away_goals,
      status: 'FT · Coach verified',
      source_url: 'coach://smart-results-import',
      source_key: `coach-import:${smartDate}:${normalizeClubName(result.home_team)}:${normalizeClubName(result.away_team)}`,
      synced_at: now,
    }));
    const keyOf = (row: OfficialLeagueResult) => `${row.match_date}|${normalizeClubName(row.home_team)}|${normalizeClubName(row.away_team)}`;
    const merged = new Map(existing.map((row) => [keyOf(row), row]));
    rows.forEach((row) => merged.set(keyOf(row), row));
    localStorage.setItem(SMART_RESULTS_STORAGE_KEY, JSON.stringify([...merged.values()]));
    setSmartStatus(`${rows.length} result${rows.length === 1 ? '' : 's'} imported. Official GDSFC data will supersede these rows when available.`);
    await refresh();
    window.setTimeout(() => setSmartImportOpen(false), 700);
  }

  const completed = useMemo(
    () =>
      matches.filter(
        (match) =>
          match.goals_for !== null &&
          match.goals_against !== null &&
          (match.competition || '').toLowerCase().includes('premiership'),
      ),
    [matches],
  );

  const calculatedTable = useMemo(() => {
    const clubName = team?.name || team?.club_name || 'Polonia Glasgow AFC';
    const rows = new Map<string, TableRow>();

    PREMIERSHIP_CLUBS.forEach((club) => {
      rows.set(club.canonicalName, {
        club: club.canonicalName,
        played: 0,
        won: 0,
        drawn: 0,
        lost: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        points: 0,
        form: [],
      });
    });

    function ensure(club: string) {
      const identity = resolveBundledClub(club);
      const canonicalClub = identity?.canonicalName || club;
      if (!rows.has(canonicalClub)) {
        rows.set(canonicalClub, {
          club: canonicalClub,
          played: 0,
          won: 0,
          drawn: 0,
          lost: 0,
          goalsFor: 0,
          goalsAgainst: 0,
          points: 0,
          form: [],
        });
      }
      return rows.get(canonicalClub)!;
    }

    completed.forEach((match) => {
      const opponent = match.opponent?.trim() || t.unknownOpponent;
      const own = ensure(clubName);
      const other = ensure(opponent);
      const gf = Number(match.goals_for ?? 0);
      const ga = Number(match.goals_against ?? 0);

      own.played += 1;
      own.goalsFor += gf;
      own.goalsAgainst += ga;
      other.played += 1;
      other.goalsFor += ga;
      other.goalsAgainst += gf;

      const ownCode = resultCode(gf, ga);
      const otherCode = resultCode(ga, gf);
      own.form.unshift(ownCode);
      other.form.unshift(otherCode);

      if (gf > ga) {
        own.won += 1;
        own.points += 3;
        other.lost += 1;
      } else if (gf === ga) {
        own.drawn += 1;
        other.drawn += 1;
        own.points += 1;
        other.points += 1;
      } else {
        own.lost += 1;
        other.won += 1;
        other.points += 3;
      }
    });

    return [...rows.values()].sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      const bDifference = b.goalsFor - b.goalsAgainst;
      const aDifference = a.goalsFor - a.goalsAgainst;
      if (bDifference !== aDifference) return bDifference - aDifference;
      return b.goalsFor - a.goalsFor;
    });
  }, [completed, team, t.unknownOpponent]);

  const officialTable = useMemo<TableRow[]>(
    () =>
      officialStandings.map((row) => ({
        club: row.club_name,
        played: row.played,
        won: row.won,
        drawn: row.drawn,
        lost: row.lost,
        goalsFor: row.goals_for,
        goalsAgainst: row.goals_against,
        points: row.points,
        form: [],
      })),
    [officialStandings],
  );

  const hasCoachImportedResults = officialResults.some((row) => row.source_key.startsWith('coach-import:'));
  const resultDerivedTable = useMemo<TableRow[]>(() => {
    if (!hasCoachImportedResults) return [];
    const rows = new Map<string, TableRow>();
    PREMIERSHIP_CLUBS.forEach((club) => rows.set(club.canonicalName, {
      club: club.canonicalName, played: 0, won: 0, drawn: 0, lost: 0,
      goalsFor: 0, goalsAgainst: 0, points: 0, form: [],
    }));
    const ensure = (name: string) => {
      const club = resolveBundledClub(name);
      const canonical = club?.canonicalName || name;
      if (!rows.has(canonical)) rows.set(canonical, { club: canonical, played: 0, won: 0, drawn: 0, lost: 0, goalsFor: 0, goalsAgainst: 0, points: 0, form: [] });
      return rows.get(canonical)!;
    };
    [...officialResults].sort((a, b) => a.match_date.localeCompare(b.match_date)).forEach((result) => {
      if (result.home_goals === null || result.away_goals === null || !String(result.status).toLowerCase().includes('ft')) return;
      const home = ensure(result.home_team);
      const away = ensure(result.away_team);
      const hg = Number(result.home_goals); const ag = Number(result.away_goals);
      home.played += 1; away.played += 1; home.goalsFor += hg; home.goalsAgainst += ag; away.goalsFor += ag; away.goalsAgainst += hg;
      const hc = resultCode(hg, ag); const ac = resultCode(ag, hg); home.form.unshift(hc); away.form.unshift(ac);
      if (hg > ag) { home.won += 1; home.points += 3; away.lost += 1; }
      else if (hg === ag) { home.drawn += 1; away.drawn += 1; home.points += 1; away.points += 1; }
      else { home.lost += 1; away.won += 1; away.points += 3; }
    });
    return [...rows.values()].sort((a, b) => b.points - a.points || (b.goalsFor - b.goalsAgainst) - (a.goalsFor - a.goalsAgainst) || b.goalsFor - a.goalsFor);
  }, [hasCoachImportedResults, officialResults]);

  const table = resultDerivedTable.length ? resultDerivedTable : (officialTable.length ? officialTable : calculatedTable);
  const usingOfficialStandings = officialTable.length > 0 && !hasCoachImportedResults;

  const internalScorers = useMemo(() => {
    const totals = new Map<string, { goals: number; assists: number; appearances: number }>();
    matches.forEach((match) => {
      match.contributions?.forEach((entry) => {
        const current = totals.get(entry.player_id) || { goals: 0, assists: 0, appearances: 0 };
        current.goals += Number(entry.goals ?? 0);
        current.assists += Number(entry.assists ?? 0);
        if (Number(entry.minutes ?? 0) > 0 || entry.started) current.appearances += 1;
        totals.set(entry.player_id, current);
      });
    });
    return players
      .map((player) => ({
        player,
        ...(totals.get(player.id) || { goals: 0, assists: 0, appearances: 0 }),
      }))
      .sort((a, b) => b.goals - a.goals || b.assists - a.assists)
      .slice(0, 8);
  }, [matches, players]);

  const officialScorerRows = useMemo(
    () =>
      officialScorers.map((entry) => {
        const player = players.find(
          (candidate) =>
            candidate.name
              .trim()
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '') ===
            entry.player_name
              .trim()
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, ''),
        );
        return {
          id: entry.id || `${entry.rank}-${entry.player_name}`,
          rank: entry.rank,
          player,
          playerName: entry.player_name,
          clubName: entry.club_name,
          goals: entry.goals,
        };
      }),
    [officialScorers, players],
  );

  const usingOfficialScorers = officialScorerRows.length > 0;

  const upcoming = useMemo(
    () =>
      [...matches]
        .filter((match) => new Date(match.match_date).getTime() > Date.now())
        .sort((a, b) => a.match_date.localeCompare(b.match_date))
        .slice(0, 6),
    [matches],
  );

  const poloniaName = team?.name || team?.club_name || 'Polonia Glasgow AFC';
  const poloniaPosition = Math.max(1, table.findIndex((row) => row.club === poloniaName) + 1);
  const poloniaRow = table.find((row) => row.club === poloniaName);
  const winRate = poloniaRow?.played
    ? Math.round((poloniaRow.won / poloniaRow.played) * 100)
    : 0;

  if (loading) return <section className="metal-card loading-panel">{t.loading}…</section>;
  if (error) return <section className="metal-card error-panel">{error}</section>;

  return (
    <div className="league-page">
      <section className="metal-card league-hero">
        <div>
          <p className="eyebrow">{t.leagueIntelligence}</p>
          <h1>Only Sport Ltd Premiership</h1>
          <p className="muted">{t.leagueSubtitle}</p>
          <div className="data-scope-note">
            <ShieldCheck size={17} />
            <span>{t.localLeagueScope}</span>
          </div>
        </div>
        <div className="league-crest-stack">
          <Trophy size={54} />
          <strong>{poloniaPosition}</strong>
          <span>{t.projectedPosition}</span>
        </div>
      </section>

      <section className={`official-sync-banner ${usingOfficialStandings || usingOfficialScorers ? 'synced' : 'waiting'}`}>
        <ShieldCheck size={18} />
        <div>
          <strong>{t.gdsfcAutoSync}</strong>
          <span>
            {officialLeagueSyncedAt
              ? `${t.lastSynced}: ${new Date(officialLeagueSyncedAt).toLocaleString()}`
              : t.awaitingCurrentSeasonFeed}
          </span>
        </div>
        {canImportResults && (
          <button type="button" className="gold-button smart-results-open" onClick={() => setSmartImportOpen(true)}>
            <ScanText size={18} /> Smart Results Import
          </button>
        )}
      </section>

      {smartImportOpen && canImportResults && (
        <section className="metal-card smart-results-import">
          <div className="smart-results-head">
            <div>
              <p className="eyebrow">GDSFC · FAST IMPORT</p>
              <h2>Smart Results Import</h2>
              <p className="muted">Screenshot → OCR → verify → publish locally. Official GDSFC sync always wins later.</p>
            </div>
            <button type="button" className="icon-steel-button" onClick={() => setSmartImportOpen(false)} aria-label="Close"><X size={18}/></button>
          </div>
          <div className="smart-results-grid">
            <div className="smart-results-source">
              <label className="smart-upload-zone">
                <Upload size={28}/>
                <strong>{smartFile ? smartFile.name : 'Choose GDSFC result screenshot'}</strong>
                <span>PNG / JPEG from Instagram</span>
                <input type="file" accept="image/*" onChange={(event) => chooseSmartFile(event.target.files?.[0] || null)} />
              </label>
              {smartPreview && <img src={smartPreview} className="smart-results-preview" alt="Selected GDSFC result card" />}
              <button type="button" className="gold-button" onClick={readSmartScreenshot} disabled={!smartFile || smartReading}>
                <ScanText size={18}/> {smartReading ? 'Reading…' : 'Read screenshot'}
              </button>
              <label className="smart-date-field"><span>Match date</span><input type="date" value={smartDate} onChange={(event) => setSmartDate(event.target.value)} /></label>
            </div>
            <div className="smart-results-review">
              <label><span>Recognised text / iPhone Live Text fallback</span><textarea value={smartText} onChange={(event) => setSmartText(event.target.value)} placeholder="Example: Haghill AFC 3-1 Polonia Glasgow AFC" /></label>
              {smartStatus && <p className="smart-import-status"><AlertTriangle size={16}/>{smartStatus}</p>}
              <div className="smart-detected-list">
                {smartParsed.map((result, index) => (
                  <div className="smart-detected-row" key={`${result.home_team}-${result.away_team}-${index}`}>
                    <ClubLogo club={result.home_team} className="smart-result-logo" />
                    <strong>{result.home_team}</strong>
                    <b>{result.home_goals}–{result.away_goals}</b>
                    <strong>{result.away_team}</strong>
                    <ClubLogo club={result.away_team} className="smart-result-logo" />
                  </div>
                ))}
                {!smartParsed.length && <p className="muted">No complete Premiership result rows detected yet.</p>}
              </div>
              <button type="button" className="gold-button smart-confirm-results" onClick={confirmSmartResults} disabled={!smartParsed.length || !smartDate}>
                <CheckCircle2 size={18}/> Confirm {smartParsed.length || ''} Result{smartParsed.length === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="league-kpi-grid">
        {[
          [poloniaRow?.points ?? 0, t.points, Medal],
          [poloniaRow?.played ?? 0, t.played, CalendarClock],
          [winRate, `${t.winRate} %`, TrendingUp],
          [poloniaRow ? poloniaRow.goalsFor - poloniaRow.goalsAgainst : 0, t.goalDifference, Goal],
        ].map(([value, label, Icon]) => (
          <button
            type="button"
            className="metal-card league-kpi smart-nav-card"
            key={String(label)}
            onClick={() => onNavigate('matches')}
          >
            <Icon size={24} />
            <strong>{value}</strong>
            <span>{label}</span>
          </button>
        ))}
      </section>

      <section className="metal-card league-table-panel">
        <div className="section-title-line">
          <div>
            <p className="eyebrow">{t.performanceTable}</p>
            <h2>{t.standings}</h2>
          </div>
          <span>
            {usingOfficialStandings ? t.officialGdsfcData : `${t.completedLeagueMatches}: ${completed.length}`}
          </span>
        </div>

        <div className="league-table-scroll">
          <table className="league-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{t.club}</th>
                <th>{t.playedShort}</th>
                <th>{t.wonShort}</th>
                <th>{t.drawnShort}</th>
                <th>{t.lostShort}</th>
                <th>{t.goalsForShort}</th>
                <th>{t.goalsAgainstShort}</th>
                <th>{t.goalDifferenceShort}</th>
                <th>{t.pointsShort}</th>
                <th>{t.form}</th>
              </tr>
            </thead>
            <tbody>
              {table.map((row, index) => {
                const isPolonia = row.club === poloniaName;
                return (
                  <tr key={row.club} className={isPolonia ? 'polonia-table-row' : ''}>
                    <td>{index + 1}</td>
                    <td>
                      <button
                        type="button"
                        className="league-club-cell league-club-link"
                        onClick={() => openClubSheet(row.club)}
                      >
                        <ClubLogo club={row.club} className="league-club-logo" />
                        <strong>{row.club}</strong>
                        <span aria-hidden="true">›</span>
                      </button>
                    </td>
                    <td>{row.played}</td>
                    <td>{row.won}</td>
                    <td>{row.drawn}</td>
                    <td>{row.lost}</td>
                    <td>{row.goalsFor}</td>
                    <td>{row.goalsAgainst}</td>
                    <td>{row.goalsFor - row.goalsAgainst}</td>
                    <td><b>{row.points}</b></td>
                    <td>
                      <div className="form-sequence">
                        {row.form.slice(0, 5).map((code, formIndex) => (
                          <i className={`form-${code.toLowerCase()}`} key={`${code}-${formIndex}`}>
                            {code}
                          </i>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!table.length && <p className="muted">{t.noLeagueResults}</p>}
      </section>

      <LeagueClubDirectory
        selectedKey={selectedClubKey}
        onSelectClub={setSelectedClubKey}
      />

      {officialResults.length > 0 && (
        <section className="metal-card official-results-panel">
          <div className="section-title-line">
            <div>
              <p className="eyebrow">GDSFC</p>
              <h2>{t.latestOfficialResults}</h2>
            </div>
            <span>{hasCoachImportedResults ? 'Coach fast import · awaiting official verification' : t.autoSynced}</span>
          </div>
          <div className="official-results-grid">
            {officialResults.slice(0, 10).map((result) => (
              <button
                type="button"
                className="official-result-row"
                key={result.source_key}
                onClick={() => onNavigate('matches')}
              >
                <span>{new Date(`${result.match_date}T12:00:00`).toLocaleDateString()}</span>
                <strong>{result.home_team}</strong>
                <b>
                  {result.home_goals === null || result.away_goals === null
                    ? result.status
                    : `${result.home_goals}–${result.away_goals}`}
                </b>
                <strong>{result.away_team}</strong>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="league-secondary-grid">
        <article className="metal-card scorer-panel">
          <div className="section-title-line">
            <div>
              <p className="eyebrow">{t.squadProduction}</p>
              <h2>{t.topScorers}</h2>
            </div>
            <Goal size={28} />
          </div>
          {usingOfficialScorers ? officialScorerRows.slice(0, 12).map((entry) => (
            <button
              className="scorer-row"
              key={entry.id}
              onClick={() => entry.player && onOpenPlayer(entry.player.id)}
              disabled={!entry.player}
            >
              <span className="scorer-rank">{entry.rank}</span>
              <span className="scorer-avatar">
                {entry.player?.photo_url ? (
                  <img src={entry.player.photo_url} alt="" />
                ) : (
                  <ClubLogo club={entry.clubName} className="scorer-fallback-club-logo" />
                )}
              </span>
              <span className="scorer-name">
                <strong>{entry.playerName}</strong>
                <small>{entry.clubName}</small>
                <em>{t.officialGdsfcScorer}</em>
              </span>
              <strong className="scorer-goals">{entry.goals}</strong>
              {entry.player && <ChevronDown className="smart-row-arrow" size={17} />}
            </button>
          )) : internalScorers.map((entry, index) => (
            <button
              className="scorer-row"
              key={entry.player.id}
              onClick={() => onOpenPlayer(entry.player.id)}
            >
              <span className="scorer-rank">{index + 1}</span>
              <span className="scorer-avatar">
                {entry.player.photo_url ? (
                  <img src={entry.player.photo_url} alt="" />
                ) : (
                  <ClubLogo club="Polonia Glasgow" className="scorer-fallback-club-logo" />
                )}
              </span>
              <span className="scorer-name">
                <strong>{entry.player.name}</strong>
                <small>{entry.player.position || '—'} · {entry.appearances} {t.apps}</small>
                <em>{entry.assists} {t.assists} · {entry.goals + entry.assists} G+A</em>
              </span>
              <strong className="scorer-goals">{entry.goals}</strong>
              <ChevronDown className="smart-row-arrow" size={17} />
            </button>
          ))}
          {!usingOfficialScorers && !internalScorers.length && <p className="muted">{t.noData}</p>}
        </article>

        <article className="metal-card fixture-panel">
          <div className="section-title-line">
            <div>
              <p className="eyebrow">{t.schedule}</p>
              <h2>{t.upcomingFixtures}</h2>
            </div>
            <CalendarClock size={28} />
          </div>
          {upcoming.map((match) => (
            <button type="button" className="fixture-row smart-fixture-row" key={match.id} onClick={() => onNavigate('matches')}>
              <div>
                <strong>{new Date(match.match_date).toLocaleDateString()}</strong>
                <small>{new Date(match.match_date).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}</small>
              </div>
              <span>VS</span>
              <div>
                <strong>{match.opponent || t.unknownOpponent}</strong>
                <small>{match.competition || '—'}</small>
              </div>
            </button>
          ))}
          {!upcoming.length && <p className="muted">{t.noUpcomingFixtures}</p>}
        </article>
      </section>
    </div>
  );
}
